<template>
    <!--
        small - always 42 px
        medium - 42 -> becomes 64 on large screens _when_ the height allows (900+px)
        larger - 42 -> becomes 64 on large screens when height > 700px
    -->
    <component
        :is="as ?? 'button'"
        ref="container"
        :class="[
            'cz2__sb-button',
            `cz2__sb-button--${resolveHeightVariant}`,
            `cz2__sb-button--${variant}`,
            `cz2__sb-button--height-${heightVariant}`,
            disabled ? 'cz2__sb-button--disabled' : '',
        ]"
        :disabled="disabled"
        @click="$emit('click')"
        :style="{ height: `${resolveHeight}px` }"
    >
        <svg
            class="cz2__sb-button-svg"
            :viewBox="viewBox"
            xmlns="http://www.w3.org/2000/svg"
        >
            <!-- Outer Border -->
            <polygon
                :points="polygonPointsOuter"
                fill="none"
                :stroke="borderColor"
                :stroke-width="12"
            />
            <!-- Inner Border -->
            <polygon
                :points="polygonPointsInner"
                fill="none"
                :stroke="background ? background : '#fff'"
                :stroke-width="6"
            />
            <!-- Button Fill -->
            <polygon
                :points="polygonPointsFill"
                :fill="buttonFillColor"
                stroke="none"
            />
        </svg>
        <div
            class="cz2__sb-button-content"
            :style="{ color: textColor }"
        >
            <slot />
        </div>
    </component>
</template>
<script>
    export default {
        props: {
            variant: {
                type: String,
                default: 'primary',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            height: {
                type: Number,
                default: 64,
            },
            heightVariant: String,
            background: String,
            as: String,
        },
        data() {
            return {
                resolveWidth: '0',
            };
        },
        computed: {
            viewBox() {
                return `0 0 ${this.resolveWidth} ${this.resolveHeight}`;
            },

            borderColor() {
                switch (this.variant) {
                case 'tertiary':
                    return '#9A7D4B'; // '#F8EBBB';
                case 'secondary':
                    return '#fff';
                case 'quaternary':
                    return '#9A7D4B';
                case 'buy':
                    return '#9A7D4B';
                case 'primary':
                default:
                    return '#112344';
                }
            },

            buttonFillColor() {
                switch (this.variant) {
                case 'secondary':
                case 'quaternary':
                    return '#fff';
                case 'tertiary':
                    return '#F8EBBB';
                case 'buy':
                    return '#FFC700';
                case 'primary':
                default:
                    return '#112344';
                }
            },
            textColor() {
                switch (this.variant) {
                case 'secondary':
                case 'tertiary':
                case 'quaternary':
                    return '#112344';
                case 'buy':
                    return '#112344';
                case 'primary':
                default:
                    return '#fff';
                }
            },

            windowWidth() {
                return this.$store.state.windowWidth;
            },

            windowHeight() {
                return this.$store.state.windowHeight;
            },

            resolveHeight() {
                if (this.heightVariant === 'small') {
                    return 42;
                }

                if (this.heightVariant === 'medium') {
                    if (this.windowWidth < 1024 || this.windowHeight < 900) {
                        return 42;
                    }

                    return 64;
                }

                if (this.heightVariant === 'large') {
                    if (this.windowWidth < 1024 || this.windowHeight < 700) {
                        return 42;
                    }

                    return 64;
                }

                if (this.windowWidth < 1024 || this.windowHeight < 700) {
                    return 42;
                }

                return this.height;
            },

            resolveHeightVariant() {
                if (this.heightVariant === 'small') {
                    return 'small';
                }

                if (this.heightVariant === 'medium') {
                    if (this.windowWidth < 1024 || this.windowHeight < 900) {
                        return 'small';
                    }

                    return 'large';
                }

                if (this.heightVariant === 'large') {
                    if (this.windowWidth < 1024 || this.windowHeight < 700) {
                        return 'small';
                    }

                    return 'large';
                }

                if (this.windowWidth < 1024 || this.windowHeight < 700) {
                    return 'small';
                }

                return this.height;
            },

            polygonPointsOuter() {
                return this.getPoints(6, 8);
            },

            polygonPointsInner() {
                return this.getPoints(6, 8);
            },

            polygonPointsFill() {
                return this.getPoints(6, 8);
            },
        },

        mounted() {
            this.resize();

            this.resizeObserver = new ResizeObserver(() => {
                this.resize();
            });

            this.resizeObserver.observe(this.$refs.container);
        },

        beforeDestroy() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        },

        methods: {
            resize() {
                this.resolveWidth = this.$refs.container.offsetWidth;
            },

            getPoints(offsetV, offsetH) {
                const w = this.resolveWidth;
                const h = this.resolveHeight;

                const p = this.resolveHeight / 2;

                const oV = offsetV;
                const oH = offsetH;

                return `
                    ${p + oH / 2}, ${oV}
                    ${w - p - oH / 2}, ${oV}
                    ${w - oH}, ${h / 2}
                    ${w - p - oH / 2}, ${h - oV}
                    ${p + oH / 2}, ${h - oV}
                    ${oH}, ${h / 2}
                `;
            },
        },
    };
</script>

<style lang="scss">
    #cz2.cz2 {
        .cz2__sb-button {
            width: 100%;

            position: relative;
            padding: 0;
            border: none;
            background: transparent;
            cursor: pointer;
            height: 64px;
            overflow: visible;

            display: inline-flex;
            align-items: center;
            justify-content: center;

            font-size: 22px !important;

            &:active:not(.cz2__sb-button--disabled) {
                font-size: 28px !important;
            }

            &:hover:not(.cz2__sb-button--disabled) {
                font-size: 28px !important;
            }

            transition: font-size 0.25s ease-out;

            &:focus {
                outline: 3px solid #f8ebbb;
                outline-offset: 3px;
            }

            &.cz2__sb-button--small {
                font-size: 16px !important;

                &:active:not(.cz2__sb-button--disabled) {
                    font-size: 20px !important;
                }

                &:hover:not(.cz2__sb-button--disabled) {
                    font-size: 20px !important;
                }
            }

            &.cz2__sb-button--medium {
                font-size: 18px !important;

                &:active:not(.cz2__sb-button--disabled) {
                    font-size: 23px !important;
                }

                &:hover:not(.cz2__sb-button--disabled) {
                    font-size: 23px !important;
                }
            }

            &.cz2__sb-button--large {
                font-size: 22px !important;

                &:active:not(.cz2__sb-button--disabled) {
                    font-size: 28px !important;
                }

                &:hover:not(.cz2__sb-button--disabled) {
                    font-size: 28px !important;
                }
            }
        }

        .cz2__sb-button--disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .cz2__sb-button-svg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: visible;
            pointer-events: none;
        }

        .cz2__sb-button-content {
            font-family: 'Proxima Nova', sans-serif;
            font-weight: bold;
            position: relative;
            z-index: 1;
            font-size: inherit;
            color: #fff;
            letter-spacing: 1px;
            text-transform: uppercase;
            text-align: center;
            margin-top: 2px;
            width: 100%;

            .loading-spinner {
                border-color: rgba(255, 255, 255, 0.5);
                border-bottom-color: #fff;
            }
        }
    }
</style>
